import { useEffect, useRef, useState } from 'react';

const C = {
  bg:'#0a0a0a', card:'#161616', cardHi:'#1f1f1f', border:'#2a2a2a',
  text:'#fafafa', muted:'#a1a1aa', dim:'#71717a',
  alex:'#ef4444', group:'#f5c518', win:'#22c55e', multi:'#8b5cf6',
};

const PALETTE = ['#fafafa', '#ef4444', '#f59e0b', '#22c55e', '#0ea5e9', '#8b5cf6', '#ec4899', '#000000'];

// Renders all strokes + accepts pointer input when canDraw=true.
// Coordinates are normalized 0..1 — independent of canvas dpr/size.
export function DrawingBoard({ strokes, canDraw, send, onClear, color, setColor, size, setSize, hint }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const active = useRef({ strokeId: null, buffer: [] });

  // Resize / dpr
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = wrap.clientWidth, h = wrap.clientHeight;
      c.width = w * dpr;
      c.height = h * dpr;
      c.style.width = w + 'px';
      c.style.height = h + 'px';
      redraw();
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  // Repaint when strokes change
  useEffect(() => { redraw(); }, [strokes]);

  function redraw() {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    const w = c.width, h = c.height;
    ctx.fillStyle = '#fafafa';
    ctx.fillRect(0, 0, w, h);
    for (const s of strokes) {
      const pts = s.points;
      if (!pts || pts.length < 1) continue;
      ctx.strokeStyle = s.color || '#000';
      ctx.lineWidth = (s.size || 4) * (Math.min(w, h) / 600);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(pts[0].x * w, pts[0].y * h);
      if (pts.length === 1) {
        // Dot
        ctx.lineTo(pts[0].x * w + 0.01, pts[0].y * h);
      } else {
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * w, pts[i].y * h);
      }
      ctx.stroke();
    }
  }

  // Pointer handlers
  function ptFromEvent(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const ev = e.touches?.[0] || e;
    const x = (ev.clientX - rect.left) / rect.width;
    const y = (ev.clientY - rect.top) / rect.height;
    return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
  }

  function onDown(e) {
    if (!canDraw) return;
    e.preventDefault();
    const p = ptFromEvent(e);
    const id = 'sk-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 6);
    active.current.strokeId = id;
    active.current.buffer = [];
    send({ type: 'draw-start', strokeId: id, color, size, points: [p] });
  }
  function onMove(e) {
    if (!canDraw || !active.current.strokeId) return;
    e.preventDefault();
    active.current.buffer.push(ptFromEvent(e));
  }
  function onUp() {
    if (!active.current.strokeId) return;
    // Flush remaining buffer first
    if (active.current.buffer.length) {
      send({ type: 'draw-extend', strokeId: active.current.strokeId, points: active.current.buffer });
      active.current.buffer = [];
    }
    send({ type: 'draw-end', strokeId: active.current.strokeId });
    active.current.strokeId = null;
  }

  // Throttled extend-flush
  useEffect(() => {
    const id = setInterval(() => {
      if (!active.current.strokeId) return;
      if (active.current.buffer.length === 0) return;
      send({ type: 'draw-extend', strokeId: active.current.strokeId, points: active.current.buffer });
      active.current.buffer = [];
    }, 60);
    return () => clearInterval(id);
  }, [send]);

  return (
    <div className="flex flex-col h-full">
      <div ref={wrapRef} className="flex-1 relative" style={{ background: '#fafafa', touchAction: 'none', minHeight: 200 }}>
        <canvas ref={canvasRef}
          onMouseDown={canDraw ? onDown : undefined} onMouseMove={canDraw ? onMove : undefined}
          onMouseUp={canDraw ? onUp : undefined} onMouseLeave={canDraw ? onUp : undefined}
          onTouchStart={canDraw ? onDown : undefined} onTouchMove={canDraw ? onMove : undefined}
          onTouchEnd={canDraw ? onUp : undefined} onTouchCancel={canDraw ? onUp : undefined}
          style={{ display: 'block', cursor: canDraw ? 'crosshair' : 'default' }} />
        {hint && (
          <div className="absolute top-2 left-2 right-2 text-center text-xs font-bold pointer-events-none" style={{ color: '#1f1f1f', textShadow: '0 1px 2px rgba(255,255,255,0.7)' }}>
            {hint}
          </div>
        )}
      </div>
      {canDraw && (
        <div className="flex items-center gap-1 px-2 py-2 border-t" style={{ background: C.card, borderColor: C.border }}>
          {PALETTE.map(p => (
            <button key={p} onClick={() => setColor(p)}
              className="w-7 h-7 rounded-full border-2 active:scale-90"
              style={{ background: p, borderColor: color === p ? C.win : C.border }} />
          ))}
          <div className="flex-1" />
          <button onClick={() => setSize(2)} className="w-7 h-7 rounded-full flex items-center justify-center active:scale-90"
            style={{ background: size === 2 ? C.cardHi : 'transparent', border: `1px solid ${size === 2 ? C.win : C.border}` }}>
            <span className="w-1 h-1 rounded-full" style={{ background: color }} />
          </button>
          <button onClick={() => setSize(4)} className="w-7 h-7 rounded-full flex items-center justify-center active:scale-90"
            style={{ background: size === 4 ? C.cardHi : 'transparent', border: `1px solid ${size === 4 ? C.win : C.border}` }}>
            <span className="w-2 h-2 rounded-full" style={{ background: color }} />
          </button>
          <button onClick={() => setSize(10)} className="w-7 h-7 rounded-full flex items-center justify-center active:scale-90"
            style={{ background: size === 10 ? C.cardHi : 'transparent', border: `1px solid ${size === 10 ? C.win : C.border}` }}>
            <span className="w-4 h-4 rounded-full" style={{ background: color }} />
          </button>
          <button onClick={onClear} className="rounded-md px-2 py-1 text-xs font-bold active:scale-95"
            style={{ background: C.cardHi, color: C.alex, border: `1px solid ${C.border}` }}>🗑</button>
        </div>
      )}
    </div>
  );
}

// Free whiteboard mini-game modal
export function WhiteboardMini({ open, onClose, strokes, send, me, drawing }) {
  const [color, setColor] = useState('#fafafa');
  const [size, setSize] = useState(4);
  useEffect(() => { setColor('#000000'); }, [open]);
  if (!open) return null;
  const opener = drawing?.openerName;
  return (
    <div className="fixed inset-0 z-40 flex flex-col" style={{ background: 'rgba(0,0,0,0.95)' }}>
      <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: C.border }}>
        <div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.muted }}>
          🎨 Whiteboard {opener ? `· geöffnet von ${opener}` : ''}
        </div>
        <div className="flex items-center gap-2">
          {(me.role === 'leader' || me.userId === drawing?.openerId) && (
            <button onClick={() => send({ type: 'whiteboard-close' })}
              className="rounded-md px-2 py-1 text-xs font-bold"
              style={{ background: C.alex, color: '#fff' }}>Beenden</button>
          )}
          <button onClick={onClose} className="text-2xl leading-none w-10 h-10 flex items-center justify-center"
            style={{ color: C.muted }}>×</button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <DrawingBoard strokes={strokes} canDraw={true} send={send}
          onClear={() => send({ type: 'draw-clear' })}
          color={color} setColor={setColor} size={size} setSize={setSize} />
      </div>
    </div>
  );
}
